import React from "react";

import {
  Drawer,
  Image,
  Box,
  Text,
  DrawerOverlay,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion, useDragControls, useMotionValue } from "framer-motion";

interface IComponent {
  onClose: () => void;
  isOpen: boolean;
  content: React.ReactNode;
  imageSrc: string;
  imageText: string;
  dataTestId: string;
}

const PerkDrawer: React.FC<IComponent> = ({ isOpen, onClose, content, imageText, imageSrc, dataTestId }) => {
  const controls = useDragControls();
  const motionValue = useMotionValue(0);
  const dragToCloseTreshold = 80;
  const isMobile = useBreakpointValue({ base: true, md: false });
  const placement: "bottom" | "right" = useBreakpointValue({ base: "bottom", md: "right" }) || "bottom";

  const handleClose = async (event) => {
    controls.start(event);
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} size="sm" placement={placement}>
        <DrawerOverlay zIndex={100} />
        <motion.div
          drag="y"
          onDragEnd={() => {
            if (motionValue.get() >= dragToCloseTreshold) {
              onClose();
            }
          }}
          dragConstraints={{
            top: 10,
            bottom: 0,
          }}
          dragElastic={{ top: 0, bottom: 0.5 }}
          dragControls={controls}
          dragListener={false}
          style={{
            y: motionValue,
            position: "relative",
            touchAction: "none",
            zIndex: 1101,
            isolation: "isolate",
          }}
        >
          <DrawerContent
            position="relative"
            m={{ base: 0, md: 4 }}
            borderTopRadius="3xl"
            borderBottomRadius={{ base: 0, md: "3xl" }}
            height={{ base: "98dvh", md: "auto" }}
          >
            <Image
              src={imageSrc}
              borderTopRadius="3xl"
              sx={{ touchAction: "none" }}
              objectFit="cover"
              aspectRatio={{ base: 1.5, md: "auto" }}
              width="auto"
            />
            <DrawerBody
              sx={{
                overflowY: "scroll",
                "::-webkit-scrollbar": {
                  width: 2,
                },
                "::-webkit-scrollbar-track": {
                  background: "transparent",
                  borderRadius: "md",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "greyscale.400",
                  borderRadius: "3xl",
                  height: "100px",
                },
                "::-webkit-scrollbar-thumb:hover": {
                  background: { md: "greyscale.400" },
                },
                scrollbarWidth: 2,
              }}
              px={8}
              py={0}
              mt={{ base: 4, md: 8 }}
              mb={{ base: 6, md: 8 }}
            >
              {/* top handle */}
              {isMobile ? (
                <Box
                  position="absolute"
                  display="flex"
                  top={2}
                  left="50%"
                  transform="translateX(-50%)"
                  height={6}
                  width="250px"
                  justifyContent="center"
                  onPointerDown={handleClose}
                  cursor="grabbing"
                >
                  <Box mt={2} height="4px" width="56px" borderRadius="md" bgColor="greyscale.500" />
                </Box>
              ) : (
                <DrawerCloseButton
                  borderRadius="sm"
                  boxSize={6}
                  top={{ base: 4, md: 8 }}
                  right={{ base: 4, md: 8 }}
                  backgroundColor="greyscale.300"
                  sx={{
                    svg: { boxSize: 2.5 },
                    _hover: { backgroundColor: "primary.200" },
                    _active: { backgroundColor: "primary.800", color: "greyscale.200" },
                  }}
                  data-testid={`btn-drawer-close-${dataTestId}`}
                />
              )}
              <Text
                fontWeight="medium"
                position="absolute"
                top={{ base: 10, md: 8 }}
                left={{ base: "50%", md: 8 }}
                transform={{ base: "translateX(-50%)", md: "none" }}
              >
                {imageText}
              </Text>
              {content}
              {/* bottom bar */}
              {isMobile && (
                <Box
                  height="5px"
                  width="134px"
                  position="absolute"
                  bottom="6px"
                  left="50%"
                  transform="translateX(-50%)"
                  borderRadius="md"
                  bgColor="greyscale.600"
                />
              )}
            </DrawerBody>
          </DrawerContent>
        </motion.div>
      </Drawer>
    </>
  );
};

export default PerkDrawer;
